import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { EmptyState } from "@/components/dashboard/DashboardCharts";
import { Age, ClientName, PriorityPill, isEscalated, rankOpenPositions } from "@/components/dashboard/queueShared";

const BOARD = "/master-staffing-board";

const Summary = ({ openCount, stale, escalated, statuses }) => (
  <ul className="ops-priority-summary" data-testid="priority-summary">
    <li data-testid="priority-summary-open">
      <strong>{openCount}</strong> open
    </li>
    <li data-testid="priority-summary-aging">
      <strong>{stale}</strong> over 10 days
    </li>
    <li data-testid="priority-summary-escalated">
      <strong>{escalated}</strong> urgent or high
    </li>
    {statuses.length > 1 &&
      statuses.map((item) => (
        <li key={item.label} data-testid={`priority-summary-status-${item.label.toLowerCase().replaceAll(" ", "-")}`}>
          <strong>{item.value}</strong> {item.label}
        </li>
      ))}
  </ul>
);

const PriorityRow = ({ row }) => (
  <li className="ops-pq-row" role="row" data-testid={`priority-row-${row.id}`}>
    <div className="ops-pq-client" role="cell">
      <ClientName name={row.client_name} />
    </div>
    <div className="ops-pq-need" role="cell">
      <span className="ops-pq-role">{row.role_name}</span>
      <span className="ops-request-id" data-testid={`priority-row-${row.id}-id`} title="Request ID">
        {row.id}
      </span>
    </div>
    <div className="ops-pq-meta" role="cell">
      <PriorityPill priority={row.priority} />
      <Age days={row.days_open} tone="amber" />
    </div>
    <div className="ops-pq-action" role="cell">
      <Link className="ops-row-action" to={`${BOARD}?request=${row.id}`} data-testid={`priority-row-${row.id}-action`}>
        Assign
      </Link>
    </div>
  </li>
);

export const PriorityPanel = ({ attention, statusBreakdown, openCount, limit = 5 }) => {
  const stale = attention?.open_over_10_days || [];
  const ranked = rankOpenPositions(stale);
  const top = ranked.slice(0, limit);
  const escalated = stale.filter((row) => isEscalated(row.priority)).length;
  const statuses = (statusBreakdown || []).filter((item) => item.value > 0);
  const remaining = ranked.length - top.length;
  return (
    <section className="ops-card ops-priority" data-testid="priority-panel">
      <div className="ops-card-heading">
        <div>
          <h2>Priority queue</h2>
          <p>Open positions waiting longest, highest priority first</p>
        </div>
        <Link className="ops-link" to={BOARD} data-testid="priority-view-queue-link">
          Full queue <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </div>
      <Summary openCount={openCount} stale={stale.length} escalated={escalated} statuses={statuses} />
      {top.length ? (
        <div className="ops-pq" role="table" aria-label="Priority queue" data-testid="priority-table">
          <div className="ops-pq-head" role="row">
            <span role="columnheader">Client</span>
            <span role="columnheader">Staffing need</span>
            <span role="columnheader" className="is-num">
              Priority · Age
            </span>
            <span role="columnheader" className="sr-only">
              Action
            </span>
          </div>
          <ul className="ops-pq-body" role="rowgroup">
            {top.map((row) => (
              <PriorityRow key={row.id} row={row} />
            ))}
          </ul>
        </div>
      ) : (
        <EmptyState
          compact
          icon={CheckCircle2}
          testId="priority-empty"
          title={openCount ? "No positions are aging" : "No open positions"}
          message={openCount ? "Every open position was opened within the last 10 days." : "Every position for this school year is covered."}
        />
      )}
      {remaining > 0 && (
        <p className="ops-priority-more" data-testid="priority-more">
          +{remaining} more aging {remaining === 1 ? "position" : "positions"} in the{" "}
          <Link to={BOARD} data-testid="priority-more-link">
            full queue
          </Link>
        </p>
      )}
    </section>
  );
};
